'use client';

import { VStack, HStack } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import {
  Badge,
  BadgeSeverity,
  isBadgeSeverityType,
} from '../../../../../components/badge';
import { normalizeSeverityLabel } from '../../../../../utils/severity-label.utils';
import { FindingCard } from './FindingCard';
import type { Finding, FindingsSearchData } from './types';
import type { OnOpenChatObject } from '../../hitl-types';

const QUERY_LABEL = 'Query:';
const RESULTS_LABEL = 'results';
const THRESHOLD_LABEL = 'threshold:';

const readStringField = (
  data: Record<string, unknown>,
  keys: readonly string[],
): string | undefined => {
  for (const key of keys) {
    const value = data[key];

    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }

  return undefined;
};

const readRecordField = (
  data: Record<string, unknown>,
  key: string,
): Record<string, unknown> | undefined => {
  const value = data[key];

  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  return value as Record<string, unknown>;
};

type FindingsRendererProps = {
  findings: Finding[];
  maxHeight?: string;
  variant?: 'compact' | 'detailed';
  // Search metadata (optional)
  query?: string;
  totalResults?: number;
  scoreThreshold?: number;
  onOpenObject?: OnOpenChatObject;
};

function FindingListSeverityBadge({ severity }: { severity: string }) {
  const normalizedSeverity = normalizeSeverityLabel(severity);

  if (!isBadgeSeverityType(normalizedSeverity)) {
    return (
      <Badge size="sm" variant="outline">
        {severity}
      </Badge>
    );
  }

  return (
    <BadgeSeverity size="sm" variant="outline" severity={normalizedSeverity}>
      {severity}
    </BadgeSeverity>
  );
}

/**
 * Unified FindingsRenderer that handles both search results and HITL views
 */
export function FindingsRenderer({
  findings,
  maxHeight = '300px',
  variant = 'compact',
  query,
  totalResults,
  scoreThreshold,
  onOpenObject,
}: FindingsRendererProps) {
  const isDetailed = variant === 'detailed';

  if (!isDetailed) {
    return (
      <VStack alignItems="start" gap={1} width="100%">
        {(query || totalResults !== undefined) && (
          <HStack gap={2} flexWrap="wrap" alignItems="center">
            {query && (
              <Text fontSize="xs" color="fg.muted" fontFamily="mono">
                {QUERY_LABEL}{' '}
                <Text as="span" color="fg.default">
                  {query}
                </Text>
              </Text>
            )}
            {totalResults !== undefined && (
              <Text fontSize="2xs" color="fg.muted">
                {totalResults} {RESULTS_LABEL}
              </Text>
            )}
            {scoreThreshold !== undefined && (
              <Text fontSize="2xs" color="fg.muted">
                {THRESHOLD_LABEL} {scoreThreshold}
              </Text>
            )}
          </HStack>
        )}

        <VStack
          gap={0.5}
          width="100%"
          maxHeight={maxHeight}
          overflowY="auto"
          overflowX="hidden"
          alignItems="stretch"
        >
          {findings.map((finding, index) => {
            const findingId = finding.findingId ?? finding.id;
            const title =
              finding.title ||
              finding.findingTitle ||
              finding.shortDescription ||
              'Finding';
            const key = findingId ?? `${title}-${index}`;
            const canOpen = Boolean(onOpenObject && findingId);

            return (
              <HStack
                key={key}
                gap={1.5}
                py={1}
                px={1.5}
                alignItems="center"
                borderRadius="sm"
                cursor={canOpen ? 'pointer' : 'default'}
                _hover={canOpen ? { bg: 'bg.subtle' } : undefined}
                onClick={
                  canOpen && findingId
                    ? () =>
                        onOpenObject?.({
                          type: 'finding',
                          id: findingId as string,
                          label: title,
                        })
                    : undefined
                }
              >
                <Text fontSize="xs" color="fg.default" truncate flex={1}>
                  {title}
                </Text>
                {finding.severity ? (
                  <FindingListSeverityBadge severity={finding.severity} />
                ) : null}
              </HStack>
            );
          })}
        </VStack>
      </VStack>
    );
  }

  return (
    <VStack alignItems="start" gap={2} width="100%">
      {/* Search metadata */}
      {(query || totalResults !== undefined) && (
        <HStack gap={2} flexWrap="wrap" alignItems="center">
          {query && (
            <Text fontSize="xs" color="fg.muted" fontFamily="mono">
              {QUERY_LABEL}{' '}
              <Text as="span" color="fg.default">
                {query}
              </Text>
            </Text>
          )}
          {totalResults !== undefined && (
            <Badge size="sm" variant="emphasis">
              {totalResults} {RESULTS_LABEL}
            </Badge>
          )}
          {scoreThreshold !== undefined && (
            <Badge size="sm" variant="outline">
              {THRESHOLD_LABEL} {scoreThreshold}
            </Badge>
          )}
        </HStack>
      )}

      {/* Findings list */}
      <VStack
        gap={isDetailed ? 3 : 1.5}
        width="100%"
        maxHeight={maxHeight}
        overflowY="auto"
        overflowX="hidden"
        alignItems="stretch"
      >
        {findings.map((finding, index) => {
          const key = finding.findingId || index;

          return (
            <FindingCard
              key={key}
              finding={finding}
              variant={variant}
              onOpenObject={onOpenObject}
            />
          );
        })}
      </VStack>
    </VStack>
  );
}

/**
 * Parse raw data into Finding format
 * Handles both compact (search results) and detailed (HITL) fields
 */
export function parseFindingData(data: Partial<Finding>): Finding | null {
  if (!data || typeof data !== 'object') return null;
  const dataRecord = data as Record<string, unknown>;
  const nestedFindingRecord = readRecordField(dataRecord, 'finding');

  // Extract all possible fields with proper typing
  return {
    // Core identifiers
    id:
      data.id ??
      readStringField(dataRecord, ['_id', 'objectId']) ??
      (nestedFindingRecord
        ? readStringField(nestedFindingRecord, ['id', '_id'])
        : undefined),
    findingId:
      data.findingId ??
      data.id ??
      readStringField(dataRecord, ['finding_id', 'findingObjectId']) ??
      (nestedFindingRecord
        ? readStringField(nestedFindingRecord, [
            'findingId',
            'finding_id',
            'id',
            '_id',
          ])
        : undefined),

    // Display fields
    title: data.title,
    findingTitle: data.findingTitle,
    shortDescription: data.shortDescription,
    severity: data.severity,
    category: data.category,
    content: data.content,
    score: data.score,
    rank: data.rank,

    // Detailed fields
    description: data.description,
    affectedEndpoint: data.affectedEndpoint,
    poc: data.poc,
    pocBlocks: data.pocBlocks ?? null,
    businessImpact: data.businessImpact,
    mitigationPlan: data.mitigationPlan,
    cvssScore: data.cvssScore,
    cvssVector: data.cvssVector,
    cvssExplanation: data.cvssExplanation,
    endpointMatched: data.endpointMatched,
    exploitationGuide: data.exploitationGuide,
    executionScript: data.executionScript,
    findingNote: data.findingNote,
  };
}

/**
 * Parse findings list from search results
 */
export function parseFindingsListData(data: {
  results?: Partial<Finding>[];
  findings?: Partial<Finding>[];
}): Finding[] | null {
  if (!data || typeof data !== 'object') return null;

  // Check for findings array in results
  const resultsArray = data.results || data.findings;

  if (Array.isArray(resultsArray)) {
    return resultsArray
      .map((item) => parseFindingData(item))
      .filter((item): item is Finding => item !== null);
  }

  return null;
}

type FindingsSearchInput = {
  results?: Partial<Finding>[];
  findings?: Partial<Finding>[];
  query?: string;
  totalResults?: number;
  scoreThreshold?: number;
};

/**
 * Parse findings search data with metadata
 */
export function parseFindingsSearchData(
  data: FindingsSearchInput,
): FindingsSearchData | null {
  if (!data || typeof data !== 'object') return null;

  const findings = parseFindingsListData(data);

  if (!findings) return null;

  return {
    findings,
    query: data.query,
    totalResults: data.totalResults,
    scoreThreshold: data.scoreThreshold,
  };
}
